import {
  CPTCode,
  CPTSearchParams,
  CandidateModifier,
  ICDTenCode,
  ICDTenSearchParams,
  ModifierCode,
  PlaceOfServiceCode,
} from "@/types/medical-codes";
import { Page } from "@/types/pagination";

import { requestData } from "./api-config";

/**
 * Search CPT codes by query string
 * @param params - Search parameters including query and optional limit
 * @returns Promise<CPTCode[]> - Array of matching CPT codes
 */
export async function searchCPTCodes(
  params: CPTSearchParams,
): Promise<CPTCode[] | null> {
  // @TODO: drop this once APISearchSelect supports pagination
  if (!params.query.trim()) return [];
  return await requestData<CPTCode[]>("medical-codes/cpt/search", "get", {
    searchParams: params,
  });
}

/**
 * Search billing modifiers from the canonical `modifier` reference table.
 *
 * Unlike CPT/ICD search, an empty query is NOT short-circuited: the modifier
 * set is small, so the endpoint returns the full list for a browsable
 * dropdown (e.g. on focus).
 * @param params - Search parameters including query and optional limit
 * @returns Promise<ModifierCode[]> - Array of matching modifiers
 */
export async function searchModifiers(
  params: CPTSearchParams,
): Promise<ModifierCode[] | null> {
  return await requestData<ModifierCode[]>(
    "medical-codes/modifiers/search",
    "get",
    {
      searchParams: params,
    },
  );
}

/**
 * Resolve an explicit set of billing-modifier codes to their descriptions.
 *
 * Unlike `searchModifiers`, this is an exact batch lookup keyed on the codes the
 * caller already holds. The printed Super Bill / note store modifiers as bare
 * codes and need the label to print beside them, and the `modifier` table
 * (~308 rows) is too large to page through the 100-capped search endpoint.
 * Codes absent from the reference set are omitted from the response.
 * @param codes - Modifier codes to resolve
 * @returns Promise<ModifierCode[] | null> - The resolved modifiers
 */
export async function lookupModifiers(
  codes: string[],
): Promise<ModifierCode[] | null> {
  // No round trip for the common case: most bills carry no modifier at all.
  if (codes.length === 0) return [];
  // catchError: false so a failure THROWS instead of resolving `null`.
  // requestData swallows errors by default, and React Query would then store
  // that `null` as a successful result under the 5-minute staleTime with
  // retry: false — silently stripping labels from every print for five minutes.
  // Throwing keeps the failure a failure, so it is neither cached nor hidden.
  return await requestData<ModifierCode[]>(
    "medical-codes/modifiers/lookup",
    "get",
    { searchParams: { codes } },
    false,
  );
}

/**
 * Resolve an explicit set of ICD-10 codes to their descriptions.
 *
 * The printed Super Bill names each diagnosis beside its code, and service
 * lines store bare codes. Same shape as `lookupModifiers`, including
 * `catchError: false` — see the note there: a swallowed failure would be cached
 * as a successful empty result and silently strip descriptions from every print
 * for the query's lifetime.
 * @param codes - ICD-10 codes to resolve
 * @returns Promise<ICDTenCode[] | null> - The resolved diagnoses
 */
export async function lookupIcdTenCodes(
  codes: string[],
): Promise<ICDTenCode[] | null> {
  if (codes.length === 0) return [];
  return await requestData<ICDTenCode[]>(
    "medical-codes/icd-10/lookup",
    "get",
    { searchParams: { codes } },
    false,
  );
}

/**
 * Search ICD-10 codes by query string, one page of the relevance-ranked list.
 * Request errors throw rather than resolve to `null`: the paged pickers cache
 * results for minutes, and a swallowed error would be cached as "no codes".
 * @param params - Search parameters including query and optional page/perPage
 * @returns Promise<Page<ICDTenCode>> - The page's codes plus the total match count
 */
export async function searchICD10Codes(
  params: ICDTenSearchParams,
): Promise<Page<ICDTenCode> | null> {
  if (!params.query.trim()) return { data: [], total: 0 };
  return await requestData<Page<ICDTenCode>>(
    "medical-codes/icd-10/search",
    "get",
    { searchParams: params },
    false,
  );
}

/**
 * List CMS Place of Service codes, optionally filtered by `q`. Unlike the CPT /
 * ICD search (which short-circuit empty queries to avoid loading huge tables),
 * this returns the full ~100-row set when `q` is empty so the picker can show
 * options immediately.
 */
export async function searchPlacesOfService(
  q: string,
): Promise<PlaceOfServiceCode[] | null> {
  const searchParams: Record<string, string> = {};
  if (q.trim()) searchParams.q = q.trim();
  return await requestData<PlaceOfServiceCode[]>(
    "medical-codes/places-of-service",
    "get",
    { searchParams },
  );
}

/**
 * Candidate (NOT eligible) modifiers for a CPT/HCPCS code. Narrows the Super
 * Bill modifier picker to the modifiers the AMA CPT Modifier Crosswalk
 * associates with the line's code. An unknown code returns an empty list. The
 * picker keeps its free-type escape, so an off-list modifier can always be added.
 * @param code - The line's billing (CPT/HCPCS) code
 * @returns Promise<CandidateModifier[] | null>
 */
export async function getCandidateModifiers(
  code: string,
): Promise<CandidateModifier[] | null> {
  return await requestData<CandidateModifier[]>(
    `medical-codes/cpt/${encodeURIComponent(code)}/candidate-modifiers`,
    "get",
  );
}

/**
 * Get a specific CPT code by its code value
 * @param code - The CPT code to retrieve
 * @returns Promise<CPTCode | null> - The CPT code or null if not found
 */
export async function getCPTCode(code: string): Promise<CPTCode | null> {
  return await requestData<CPTCode>(
    `medical-codes/cpt/${encodeURIComponent(code)}`,
    "get",
  );
}

/**
 * Get a specific ICD-10 code by its code value
 * @param code - The ICD-10 code to retrieve
 * @returns Promise<ICDTenCode | null> - The ICD-10 code or null if not found
 */
export async function getICDTenCode(code: string): Promise<ICDTenCode | null> {
  return await requestData<ICDTenCode>(
    `medical-codes/icd-10/${encodeURIComponent(code)}`,
    "get",
  );
}
